import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAdmin } from '../context/AdminContext';
import { t } from '../lib/translations';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Trash2, ArrowLeft, Users, Calendar, Mail, Phone, Building2,
  MessageSquare, ChevronDown, Lock, Eye, EyeOff, Download, BarChart3, Filter,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-800 border-blue-200',
  contacted: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  converted: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
};

function AdminLoginGate({ onSuccess }) {
  const { language } = useLanguage();
  const { login } = useAdmin();
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError('');
    try {
      await login(password);
      onSuccess();
    } catch {
      setError(language === 'pl' ? 'Nieprawidłowe hasło' : 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="admin-login-gate">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-lg">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-[#1B3A7B] text-white flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-[#1B3A7B]">
            {language === 'pl' ? 'Panel administratora' : 'Admin Panel'}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {language === 'pl' ? 'Podaj hasło, aby uzyskać dostęp' : 'Enter password to access'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Input
              type={showPw ? 'text' : 'password'}
              placeholder={language === 'pl' ? 'Hasło administratora' : 'Admin password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              className={`h-12 pr-10 border-slate-300 focus:border-[#1B3A7B] focus:ring-[#1B3A7B] ${error ? 'border-red-400' : ''}`}
              data-testid="admin-password-input"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPw(!showPw)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              data-testid="toggle-password-visibility"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {error && (
            <p className="text-red-500 text-sm font-medium" data-testid="admin-login-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || !password.trim()}
            className={`w-full h-12 rounded-md font-semibold text-base transition-all ${
              loading || !password.trim()
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-[#1B3A7B] hover:bg-[#142d63] text-white shadow-md hover:shadow-lg'
            }`}
            data-testid="admin-login-btn"
          >
            {loading
              ? (language === 'pl' ? 'Logowanie...' : 'Logging in...')
              : (language === 'pl' ? 'Zaloguj się' : 'Log in')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function LeadsAdmin() {
  const { language } = useLanguage();
  const { isAdmin, token, loading: authLoading } = useAdmin();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/leads`, { headers: authHeaders });
      setLeads(res.data);
    } catch {
      toast.error(t('common.error', language));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && token) fetchLeads();
  }, [isAdmin, token]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}?status=${status}`, null, { headers: authHeaders });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l));
      toast.success(language === 'pl' ? 'Status zaktualizowany' : 'Status updated');
    } catch {
      toast.error(t('common.error', language));
    }
  };

  const deleteLead = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: authHeaders });
      setLeads(prev => prev.filter(l => l.id !== id));
      toast.success(t('common.deleted', language));
    } catch {
      toast.error(t('common.error', language));
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-GB', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const statusLabel = (status) => t(`leads.status${status.charAt(0).toUpperCase() + status.slice(1)}`, language);
  const statuses = ['new', 'contacted', 'converted', 'rejected'];
  const countByStatus = (s) => leads.filter(l => l.status === s).length;
  const filteredLeads = filterStatus === 'all' ? leads : leads.filter(l => l.status === filterStatus);

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/leads/export-csv`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leads-export.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(language === 'pl' ? 'CSV wyeksportowany' : 'CSV exported');
    } catch {
      toast.error(t('common.error', language));
    }
  };

  if (authLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#1B3A7B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return <AdminLoginGate onSuccess={() => {}} />;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="leads-admin-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A7B]">
            {t('leads.title', language)}
          </h1>
          <p className="text-slate-500 mt-1">{t('leads.subtitle', language)}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 rounded-md text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-1.5"
            data-testid="leads-dashboard-btn"
          >
            <BarChart3 className="w-4 h-4" />
            Dashboard
          </button>
          <button
            onClick={exportCSV}
            className="px-4 py-2 rounded-md text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-1.5"
            data-testid="leads-export-csv-btn"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 rounded-md text-sm font-medium border-2 border-[#1B3A7B] text-[#1B3A7B] hover:bg-[#1B3A7B] hover:text-white transition-all flex items-center gap-1.5"
            data-testid="leads-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('leads.backToCalc', language)}
          </button>
        </div>
      </div>

      {/* Status summary cards (clickable filter) */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
        <div
          onClick={() => setFilterStatus('all')}
          className={`bg-white rounded-xl border p-4 text-center cursor-pointer transition-all ${filterStatus === 'all' ? 'border-[#1B3A7B] ring-1 ring-[#1B3A7B]' : 'border-slate-200 hover:border-slate-300'}`}
          data-testid="filter-all"
        >
          <div className="font-mono-price text-2xl font-bold text-[#1B3A7B]">{leads.length}</div>
          <div className="text-xs font-medium text-slate-500 mt-1">{language === 'pl' ? 'Wszystkie' : 'All'}</div>
        </div>
        {statuses.map(s => (
          <div
            key={s}
            onClick={() => setFilterStatus(filterStatus === s ? 'all' : s)}
            className={`bg-white rounded-xl border p-4 text-center cursor-pointer transition-all ${filterStatus === s ? 'border-[#1B3A7B] ring-1 ring-[#1B3A7B]' : 'border-slate-200 hover:border-slate-300'}`}
            data-testid={`filter-${s}`}
          >
            <div className="font-mono-price text-2xl font-bold text-[#1B3A7B]">{countByStatus(s)}</div>
            <div className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_COLORS[s]}`}>
              {statusLabel(s)}
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#1B3A7B] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-20">
          <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">{t('leads.empty', language)}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredLeads.map((lead) => (
            <div
              key={lead.id}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              data-testid={`lead-item-${lead.id}`}
            >
              <div
                className="p-5 flex items-start justify-between cursor-pointer"
                onClick={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                    <span className="font-semibold text-slate-800 text-base">{lead.name}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_COLORS[lead.status]}`}>
                      {statusLabel(lead.status)}
                    </span>
                    {lead.monthly_total > 0 && (
                      <span className="font-mono-price text-sm font-bold text-[#E8941A]">
                        {lead.monthly_total} {t('common.currency', language)}/{t('common.perMonth', language)}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                    <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {lead.email}</span>
                    {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {lead.phone}</span>}
                    {lead.company_name && <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {lead.company_name}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5 text-xs text-slate-400">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(lead.created_at)}
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform flex-shrink-0 mt-1 ${expandedId === lead.id ? 'rotate-180' : ''}`} />
              </div>

              {expandedId === lead.id && (
                <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/50 animate-fade-in">
                  {lead.message && (
                    <div className="mb-4">
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1 mb-1">
                        <MessageSquare className="w-3.5 h-3.5" />
                        {language === 'pl' ? 'Wiadomość' : 'Message'}
                      </span>
                      <p className="text-sm text-slate-700 bg-white rounded-lg p-3 border border-slate-200">{lead.message}</p>
                    </div>
                  )}
                  {lead.accounting_form && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600 mb-4">
                      <span>{t(`step1.${lead.accounting_form}`, language)}</span>
                      {lead.legal_entity && <span>{t(`step1.entities.${lead.legal_entity}`, language)}</span>}
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <Select value={lead.status} onValueChange={(val) => updateStatus(lead.id, val)}>
                        <SelectTrigger className="h-9 border-slate-300 bg-white" data-testid={`lead-status-select-${lead.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statuses.map(s => (
                            <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteLead(lead.id); }}
                      className="h-9 px-3 rounded-md text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 flex items-center gap-1.5 transition-colors"
                      data-testid={`delete-lead-${lead.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {t('common.delete', language)}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
